// "Raporlar" — the staff report screen, brought BACK (owner 2026-09-09).
//
// It was removed once because the old version was a per-brand rollup that repeated Genel
// Bakış exactly. This one is not that: its subject is the INDIVIDUAL agent ("Temsilci
// Raporu"). Genel Bakış answers "how is the team doing this period"; this screen answers
// "how is THIS person doing" — the same card an agent sees as Gelişimim, and the one a
// supervisor previously had to reach by hunting the ranking table for a "Panosu" link.
//
// The period bar lives HERE and is handed down to <AgentReport embedded>, so the picker at
// the top of the page governs the card below it instead of the two disagreeing.
//
// The roster comes from /api/agents (every ACTIVE agent in the caller's scope), NOT from the
// overview's ranking rows: an agent with no calls this period must still be selectable — an
// empty report is itself the answer to "why is this person not being scored".

import { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import type { AgentOption, Meta, PanelUser } from '../types';
import { useAsync, usePeriodPicker } from '../hooks';
import { Empty, Icon, Loading, Note, PageHead, Panel } from '../ui';
import { navigate } from '../App';
import AgentReport from './AgentReport';
import PeriodBar from './PeriodBar';

export default function Reports({ user, meta, brand }: {
  user: PanelUser; meta: Meta | null;
  /** The topbar's global brand filter; '' = every brand this user may see (Rule 9b). */
  brand: string;
}) {
  const picker = usePeriodPicker(meta);
  const agents = useAsync<AgentOption[]>(() => api.getAgents(), []);
  const [agentId, setAgentId] = useState<number | null>(null);

  // The topbar brand filter narrows the roster, exactly as it narrows every other screen.
  // Sorted in Turkish so İ/ı land where a Turkish reader expects them.
  const list = useMemo(() => {
    const rows = (agents.data ?? []).filter((a) => !brand || a.brandKey === brand);
    return [...rows].sort((a, b) => a.displayName.localeCompare(b.displayName, 'tr'));
  }, [agents.data, brand]);

  // A selection the current filter no longer contains must not keep rendering under the new
  // brand's heading — drop back to "nobody selected" rather than showing a stale card.
  useEffect(() => {
    if (agentId !== null && !list.some((a) => a.id === agentId)) setAgentId(null);
  }, [list, agentId]);

  const picked = list.find((a) => a.id === agentId) ?? null;
  const brandName = meta?.brands.find((b) => b.key === brand)?.name ?? null;

  return (
    <>
      <PageHead
        icon="insights"
        title="Raporlar"
        subtitle={`Temsilci bazında kalite raporu${brandName ? ` · ${brandName}` : ''}`}
        right={<PeriodBar picker={picker} />}
      />

      <Panel
        icon="groups"
        title="Temsilci Raporu"
        hint={list.length ? `${list.length} temsilci` : undefined}
      >
        <div className="panel-body">
          <div className="filters">
            <div className="field" style={{ minWidth: 260 }}>
              <label htmlFor="ra">Temsilci</label>
              <select
                id="ra" className="inp"
                value={agentId === null ? '' : String(agentId)}
                onChange={(ev) => setAgentId(ev.target.value ? Number(ev.target.value) : null)}
              >
                <option value="">Temsilci seçin…</option>
                {list.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName} · {a.brandKey}
                  </option>
                ))}
              </select>
            </div>
            {picked ? (
              <button
                className="btn sm" type="button"
                onClick={() => navigate(`agent/${picked.id}`)}
                title="Bu raporu tek başına aç"
              >
                <Icon name="arrow_forward" /><span>Tam sayfa aç</span>
              </button>
            ) : null}
          </div>
          {agents.error ? <Note error>{agents.error}</Note> : null}
        </div>
      </Panel>

      {!agents.data && !agents.error ? <Loading /> : null}
      {agents.data && list.length === 0 ? (
        <Empty>Bu markada aktif temsilci yok.</Empty>
      ) : null}

      {picked ? (
        <>
          <PageHead
            icon="workspace_premium"
            title={picked.displayName}
            subtitle={`${picked.brandKey} · seçili dönemin kalite raporu`}
          />
          {/* Same card as the agent's own Gelişimim and as #/agent/:id — one component, so a
              change to the report is a change everywhere it is read. */}
          <AgentReport agentId={picked.id} user={user} meta={meta} picker={picker} embedded />
        </>
      ) : list.length > 0 ? (
        <Empty>Raporu görüntülemek için bir temsilci seçin.</Empty>
      ) : null}
    </>
  );
}
